import { z } from 'zod';
import {
  ChoiceBlock,
  ConfirmBlock,
  DateBlock,
  FieldRule,
  FormBlock,
  InputBlock,
  isInputBlock,
  NumberBlock,
  TextBlock,
} from './formDescriptor.types';

const DEFAULT_MESSAGES: Record<string, string> = {
  required: 'Pflichtfeld',
  min: 'Wert ist zu klein',
  max: 'Wert ist zu groß',
  minLength: 'Eingabe ist zu kurz',
  maxLength: 'Eingabe ist zu lang',
  pattern: 'Eingabe hat das falsche Format',
  email: 'Keine gültige E-Mail-Adresse',
  dateMin: 'Datum liegt zu früh',
  dateMax: 'Datum liegt zu spät',
  choice: 'Bitte eine Auswahl treffen',
  numeric: 'Bitte eine Zahl eingeben',
  date: 'Bitte ein gültiges Datum wählen',
};

const message = (rule: FieldRule) =>
  rule.message || DEFAULT_MESSAGES[rule.kind] || 'Ungültige Eingabe';

const findRule = (rules: FieldRule[] | undefined, kind: FieldRule['kind']) =>
  rules?.find((r) => r.kind === kind);

const numberValue = (rule: FieldRule | undefined): number | undefined =>
  rule && typeof rule.value === 'number' ? rule.value : undefined;

function buildTextSchema(block: TextBlock): z.ZodTypeAny {
  const rules = block.rules;
  const requiredRule = findRule(rules, 'required');

  let schema = z.string();

  if (requiredRule) {
    schema = schema.min(1, message(requiredRule));
  }

  const minLength = findRule(rules, 'minLength');
  if (numberValue(minLength) !== undefined) {
    schema = schema.min(numberValue(minLength)!, message(minLength!));
  }

  const maxLength = findRule(rules, 'maxLength');
  if (numberValue(maxLength) !== undefined) {
    schema = schema.max(numberValue(maxLength)!, message(maxLength!));
  }

  const pattern = findRule(rules, 'pattern');
  if (pattern && typeof pattern.value === 'string') {
    schema = schema.regex(new RegExp(pattern.value), message(pattern));
  }

  const email = findRule(rules, 'email');
  if (email) {
    schema = schema.email(message(email));
  }

  if (requiredRule) {
    return schema;
  }

  return z.union([z.literal(''), schema]).optional();
}

function buildNumberSchema(block: NumberBlock): z.ZodTypeAny {
  const rules = block.rules;
  const requiredRule = findRule(rules, 'required');
  const min = findRule(rules, 'min');
  const max = findRule(rules, 'max');

  return z
    .number({ invalid_type_error: DEFAULT_MESSAGES.numeric })
    .nullish()
    .superRefine((value, ctx) => {
      if (value === null || value === undefined) {
        if (requiredRule) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: message(requiredRule) });
        }
        return;
      }

      const minValue = numberValue(min);
      if (minValue !== undefined && value < minValue) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: message(min!) });
      }

      const maxValue = numberValue(max);
      if (maxValue !== undefined && value > maxValue) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: message(max!) });
      }
    });
}

function buildChoiceSchema(block: ChoiceBlock): z.ZodTypeAny {
  const selectable = block.options.filter((o) => !o.disabled).map((o) => o.value);
  const requiredRule = findRule(block.rules, 'required');

  return z.string().superRefine((value, ctx) => {
    if (!value) {
      if (requiredRule) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: message(requiredRule) });
      }
      return;
    }
    if (!selectable.includes(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: DEFAULT_MESSAGES.choice });
    }
  });
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function toIsoDate(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso || !ISO_DATE.test(iso)) return null;
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function buildDateSchema(block: DateBlock): z.ZodTypeAny {
  const rules = block.rules;
  const requiredRule = findRule(rules, 'required');
  const dateMin = findRule(rules, 'dateMin');
  const dateMax = findRule(rules, 'dateMax');

  return z
    .date({ invalid_type_error: DEFAULT_MESSAGES.date })
    .nullish()
    .superRefine((value, ctx) => {
      if (value === null || value === undefined) {
        if (requiredRule) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: message(requiredRule) });
        }
        return;
      }

      if (Number.isNaN(value.getTime())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: DEFAULT_MESSAGES.date });
        return;
      }

      const iso = toIsoDate(value);

      if (dateMin && typeof dateMin.value === 'string' && iso < dateMin.value) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: message(dateMin) });
      }
      if (dateMax && typeof dateMax.value === 'string' && iso > dateMax.value) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: message(dateMax) });
      }
    });
}

function buildConfirmSchema(block: ConfirmBlock): z.ZodTypeAny {
  const requiredRule = findRule(block.rules, 'required');
  if (!requiredRule) {
    return z.boolean();
  }
  return z.literal(true, {
    errorMap: () => ({ message: message(requiredRule) }),
  });
}

function buildBlockSchema(block: InputBlock): z.ZodTypeAny {
  switch (block.type) {
    case 'text':
      return buildTextSchema(block);
    case 'number':
      return buildNumberSchema(block);
    case 'choice':
      return buildChoiceSchema(block);
    case 'date':
      return buildDateSchema(block);
    case 'confirm':
      return buildConfirmSchema(block);
  }
}

export function buildFormSchema(blocks: FormBlock[]): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  blocks.filter(isInputBlock).forEach((block) => {
    shape[block.field] = buildBlockSchema(block);
  });

  return z.object(shape);
}

export type FormValue = string | boolean | number | Date | null;

export function buildDefaultValues(blocks: FormBlock[]): Record<string, FormValue> {
  const values: Record<string, FormValue> = {};

  blocks.filter(isInputBlock).forEach((block) => {
    switch (block.type) {
      case 'confirm':
        values[block.field] = block.default ?? false;
        break;
      case 'number':
        values[block.field] = block.default ?? null;
        break;
      case 'date':
        values[block.field] = toDate(block.default);
        break;
      default:
        values[block.field] = block.default ?? '';
    }
  });

  return values;
}

export function toSubmitValues(
  blocks: FormBlock[],
  values: Record<string, FormValue>
): Record<string, string | boolean | number | null> {
  const payload: Record<string, string | boolean | number | null> = {};

  blocks.filter(isInputBlock).forEach((block) => {
    const value = values[block.field];

    if (block.type === 'date') {
      payload[block.field] = value instanceof Date ? toIsoDate(value) : null;
      return;
    }

    payload[block.field] = value as string | boolean | number | null;
  });

  return payload;
}
